import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Button,
  Box,
  Typography,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { extractErrorMessage } from '../../utils/errorUtils';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface NotificationAction {
  label: string;
  action: () => void;
  color?: 'primary' | 'secondary' | 'inherit';
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  duration?: number;
  actions?: NotificationAction[];
  retryAction?: () => void;
  persistent?: boolean;
}

interface NotificationContextType {
  showNotification: (notification: Omit<Notification, 'id'>) => void;
  hideNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'success':
      return <SuccessIcon />;
    case 'error':
      return <ErrorIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'info':
      return <InfoIcon />;
  }
};

const getNotificationColor = (type: NotificationType) => {
  switch (type) {
    case 'success':
      return 'success';
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
  }
};

const getErrorMessage = (error: any): string => {
  return extractErrorMessage(error);
};

const getErrorTitle = (error: any): string => {
  if (error?.response?.status === 404) return 'Resource Not Found';
  if (error?.response?.status === 403) return 'Access Denied';
  if (error?.response?.status === 500) return 'Server Error';
  if (error?.response?.status >= 400) return 'Request Failed';
  return 'Error';
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    setNotifications(prev => [...prev, { ...notification, id }]);
  }, []);

  const hideNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const handleClose = (id: string) => {
    hideNotification(id);
  };

  const handleRetry = (notification: Notification) => {
    if (typeof notification.retryAction === 'function') {
      notification.retryAction();
    } else {
      // Safety: don't crash if retryAction was incorrectly provided as a non-function
      console.warn('Notification retryAction is not a function:', notification.retryAction);
    }
    hideNotification(notification.id);
  };

  return (
    <NotificationContext.Provider value={{ showNotification, hideNotification, clearAll }}>
      {children}
      {notifications.map((notification) => (
        <Snackbar
          key={notification.id}
          open={true}
          autoHideDuration={notification.persistent ? undefined : (notification.duration || 6000)}
          onClose={() => handleClose(notification.id)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ mt: 8 }}
        >
          <Alert
            severity={getNotificationColor(notification.type)}
            icon={getNotificationIcon(notification.type)}
            onClose={() => handleClose(notification.id)}
            sx={{
              minWidth: 400,
              maxWidth: 500,
              '& .MuiAlert-message': {
                width: '100%',
                maxWidth: '100%',
                wordWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
              },
            }}
            action={
              <IconButton
                size="small"
                onClick={() => handleClose(notification.id)}
                sx={{ color: 'inherit' }}
              >
                <CloseIcon />
              </IconButton>
            }
          >
            <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
              {notification.title}
            </AlertTitle>
            <Typography variant="body2" sx={{ 
              lineHeight: 1.5, 
              wordWrap: 'break-word', 
              whiteSpace: 'pre-wrap', 
              overflowWrap: 'break-word',
              maxWidth: '100%',
              mb: ((notification.actions && notification.actions.length > 0) || notification.retryAction) ? 1 : 0 
            }}>
              {notification.message}
            </Typography>
            {(notification.actions && notification.actions.length > 0) || notification.retryAction ? (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                {notification.retryAction && (
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => handleRetry(notification)}
                    sx={{ color: 'inherit', textTransform: 'none' }}
                  >
                    Retry
                  </Button>
                )}
                {notification.actions?.map((action, index) => (
                  <Button
                    key={index}
                    size="small"
                    onClick={action.action}
                    color={action.color || 'inherit'}
                    sx={{ color: 'inherit', textTransform: 'none' }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            ) : null}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};

// Utility functions for common error patterns
export const createErrorNotification = (
  error: any,
  retryAction?: () => void,
  customMessage?: string
): Omit<Notification, 'id'> => ({
  type: 'error',
  title: getErrorTitle(error),
  message: customMessage || getErrorMessage(error),
  retryAction: typeof retryAction === 'function' ? retryAction : undefined,
  persistent: false,
  duration: 8000,
});

export const createSuccessNotification = (
  title: string,
  message: string,
  actions?: NotificationAction[]
): Omit<Notification, 'id'> => ({
  type: 'success',
  title,
  message,
  actions,
  duration: 4000,
});

export const createWarningNotification = (
  title: string,
  message: string,
  actions?: NotificationAction[]
): Omit<Notification, 'id'> => ({
  type: 'warning',
  title,
  message,
  actions,
  duration: 6000,
});

export const createInfoNotification = (
  title: string,
  message: string,
  actions?: NotificationAction[]
): Omit<Notification, 'id'> => ({
  type: 'info',
  title,
  message,
  actions,
  duration: 5000,
}); 